'use client';

import {
  createContext,
  FormEvent,
  ReactNode,
  startTransition,
  use,
  useActionState,
} from 'react';
import { Button, ButtonProps, InlineMessage } from '@invisionag/components';
import { Form as AriaForm } from 'react-aria-components';
import { ValidationErrors } from '@react-types/shared';
import { useTranslation } from '@invisionag/next/i18n';
import { Result } from '@invisionag/next/result';

export type { ValidationErrors };

export interface FormError {
  message?: string;
  validationErrors?: ValidationErrors;
}

export type FormResult = Result<void | { message?: string }, FormError>;

export interface FormProps {
  action: (formData: FormData) => Promise<FormResult>;
  className?: string;
  children?: ReactNode;
}

export function Form({ action, className, children }: FormProps) {
  const { t } = useTranslation();

  const [result, formAction, submitting] = useActionState(
    async (_: FormResult | null, formData: FormData): Promise<FormResult> => {
      try {
        return await action(formData);
      } catch (error) {
        console.error(error);
        return Result.Err({ message: t('form.error.unknown') });
      }
    },
    null,
  );

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    startTransition(() => formAction(new FormData(event.currentTarget)));
  }

  const variant = result?.ok === false ? 'error' : 'success';
  const message =
    result?.ok === false ? result.err.message : result?.val?.message;
  const validationErrors =
    result?.ok === false ? result.err.validationErrors : undefined;

  return (
    <AriaForm
      method="post"
      validationBehavior="aria"
      className={className}
      validationErrors={validationErrors}
      onSubmit={handleSubmit}
    >
      <Context value={{ variant, message, submitting }}>{children}</Context>
    </AriaForm>
  );
}

const Context = createContext<{
  variant?: 'success' | 'error';
  message?: string;
  submitting?: boolean;
}>({});

export type SubmitButtonProps = Omit<ButtonProps, 'type'>;

export function SubmitButton({ children, ...props }: SubmitButtonProps) {
  const { submitting } = use(Context);
  return (
    <Button variant="primary" type="submit" loading={submitting} {...props}>
      {children}
    </Button>
  );
}

export function FormMessage() {
  const { message, variant } = use(Context);
  if (!message) return null;
  return <InlineMessage variant={variant}>{message}</InlineMessage>;
}
